import { Divider, Stack } from '@mui/material';
import { useCallback, memo, useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HikStorageParams } from '@thive/fddm-types';
import { degToRad, radToDeg } from 'three/src/math/MathUtils.js';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { WorkspaceMode } from '@/modules/common/types/general';
import {
  getMultiSelectDisplayValue,
  MultiSelectInput,
  NumberInput,
} from '@/modules/common/components/inputs';
import { NumUtils } from '@/modules/common/utils';
import { computeRectanglesBoundingBoxExtent } from '@/modules/common/helpers/boundingBox';
import { primitiveArraysHaveEqualContents } from '@/modules/common/helpers/array';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { LoadingServiceFactory } from '@/modules/app/service/LoadingServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useReadSelectedTemplates } from '@/modules/floorplanDesign/hooks';
import {
  areasToFlatCollection,
  hasNoTemplates,
  isHighwayArea,
} from '@/modules/floorplanDesign/helpers';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { TpsServiceFactory } from '../service/TpsServiceFactory';
import { TemplateDimensions } from './TemplateDimensions';

function HikTemplateParametersComponent() {
  const { t } = useTranslation();
  const mode = useAppStore((state) => state.mode);
  const loadingSvc = useMemo(() => LoadingServiceFactory.getInstance().createLoadingService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const tpsSvc = TpsServiceFactory.getInstance().createTpsService();
  const selectedGroups = useReadSelectedTemplates();

  const hikGroup = useMemo(() => {
    if (hasNoTemplates(selectedGroups)) return;

    const group = selectedGroups?.hikStorages?.[0];
    if (!group) return;

    return group;
  }, [selectedGroups]);

  const newGenerationParams = useMemo(() => {
    if (!hikGroup) return;

    return hikGroup.params;
  }, [hikGroup]);

  const [crossAislesAfterBays, setCrossAislesAfterBays] = useState<number[]>([]);

  useEffect(() => {
    // PLZ FIX ME
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCrossAislesAfterBays(newGenerationParams?.crossAislesAfterBays || []);
  }, [newGenerationParams?.crossAislesAfterBays]);

  const crossAislesOptions = useMemo(() => {
    if (!newGenerationParams) return [];
    return Array.from(Array(newGenerationParams.numBays - 1), (el, i) => i + 1).map((val) => ({
      label: t('interface:properties.template.crossAisles.option_label', {
        value: val,
      }),
      value: String(val),
    }));
  }, [newGenerationParams, t]);

  const crossAislesDisplayValue = useMemo(() => {
    if (!newGenerationParams) return '';
    return getMultiSelectDisplayValue(
      crossAislesAfterBays.map((val) => ({
        name: String(val),
        id: String(val),
      })),
      crossAislesAfterBays.map((item) => String(item)),
    );
  }, [crossAislesAfterBays, newGenerationParams]);

  const dimensions = useFloorplanDesignStore((state) => {
    if (!hikGroup) return;

    const areasInGroupIds = hikGroup.group.areaIds;
    const areasInGroups = areasToFlatCollection(state.getAreasByIds(areasInGroupIds));

    const rectangles = areasInGroups.flatMap((item) =>
      isHighwayArea(item) ? item.segments.flatMap(RectangleUtils.fromSegment) : [item.rectangle],
    );

    if (rectangles.length === 0) return null;

    const box = computeRectanglesBoundingBoxExtent(rectangles);
    return { x: box.x, y: box.y, length: box.width, width: box.height };
  });

  const regenerate = useCallback(
    async (params?: Partial<HikStorageParams>) => {
      if (!dimensions || !newGenerationParams || !hikGroup) return;

      loadingSvc.showLoadContainer('TRANSPARENT');

      await tpsSvc.regenerateHikTemplate({
        ...hikGroup,
        params: { ...newGenerationParams, ...params },
      });

      loadingSvc.hideLoadContainer();
    },
    [dimensions, newGenerationParams, hikGroup, tpsSvc, loadingSvc],
  );

  const onNumAislesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.hik.numAisles', newValue);
      regenerate({ numAisles: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onNumBaysChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.hik.numBays', newValue);

      // remove cross aisles that exceed the new number of bays
      const newCrossAislesAfterBays = crossAislesAfterBays.filter(
        (crossAisle) => crossAisle < newValue,
      );

      regenerate({ numBays: newValue, crossAislesAfterBays: newCrossAislesAfterBays });
    },
    [crossAislesAfterBays, fddmSvc, regenerate],
  );

  const onNumShelvesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.hik.numShelves', newValue);
      regenerate({ numShelves: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onCrossAisleOptionClick = useCallback(
    (value: string) => {
      if (!newGenerationParams) return;

      const newValue = Number(value);
      const { numBays } = newGenerationParams;
      let updated = [...crossAislesAfterBays];
      updated = updated.filter((crossAisle) => crossAisle <= numBays);
      updated = updated.includes(newValue)
        ? updated.filter((item) => item !== newValue)
        : [...updated, Number(newValue)];
      updated.sort((a, b) => a - b);

      setCrossAislesAfterBays(updated);
    },
    [crossAislesAfterBays, newGenerationParams],
  );

  const onCrossAisleCommit = useCallback(() => {
    if (!newGenerationParams) return;
    if (
      primitiveArraysHaveEqualContents(
        newGenerationParams.crossAislesAfterBays,
        crossAislesAfterBays,
      )
    )
      return;

    regenerate({ crossAislesAfterBays });
  }, [crossAislesAfterBays, newGenerationParams, regenerate]);

  // Makes sure the onCrossAisleCommit is called when the component unmounts
  const onCrossAisleCommitRef = useRef(onCrossAisleCommit);
  useEffect(() => {
    onCrossAisleCommitRef.current = onCrossAisleCommit;
  }, [onCrossAisleCommit]);
  useEffect(
    () => () => {
      onCrossAisleCommitRef.current();
    },
    [],
  );

  const onCenterXChange = useCallback(
    (newValue: number) => {
      regenerate({ centerX: NumUtils.mmToMeters(newValue) });
    },
    [regenerate],
  );

  const onCenterYChange = useCallback(
    (newValue: number) => {
      regenerate({ centerY: NumUtils.mmToMeters(newValue) });
    },
    [regenerate],
  );

  const onAngleChange = useCallback(
    (newValue: number) => {
      regenerate({ angle: degToRad(newValue) });
    },
    [regenerate],
  );

  if (!newGenerationParams || !dimensions) return null;

  return (
    <Stack spacing={1} divider={<Divider />}>
      <TemplateDimensions
        x={newGenerationParams.centerX}
        y={newGenerationParams.centerY}
        length={dimensions.length}
        width={dimensions.width}
        angle={radToDeg(newGenerationParams.angle)}
        onXChange={onCenterXChange}
        onYChange={onCenterYChange}
        onAngleChange={onAngleChange}
      />
      <Accordion
        title={t('interface:properties.template_title.hik', 'Hik')}
        unmountOnExit
        defaultExpanded
        sx={{ px: 2 }}
      >
        <Stack direction='column' spacing={1}>
          <PropertiesLabel i18nkey='interface:properties.template.aisles'>
            <NumberInput
              value={newGenerationParams.numAisles}
              onChange={onNumAislesChange}
              disabled={mode !== WorkspaceMode.EDITABLE}
              minValue={1}
            />
          </PropertiesLabel>
          <PropertiesLabel i18nkey='interface:properties.template.bays'>
            <NumberInput
              value={newGenerationParams.numBays}
              onChange={onNumBaysChange}
              disabled={mode !== WorkspaceMode.EDITABLE}
              minValue={1}
            />
          </PropertiesLabel>
          <PropertiesLabel i18nkey='interface:properties.template.shelves'>
            <NumberInput
              value={newGenerationParams.numShelves}
              onChange={onNumShelvesChange}
              minValue={1}
              disabled={mode !== WorkspaceMode.EDITABLE}
            />
          </PropertiesLabel>
          <PropertiesLabel i18nkey='interface:properties.template.crossAisles.input_label'>
            <MultiSelectInput
              options={crossAislesOptions}
              selectedOptions={crossAislesAfterBays.map((item) => String(item))}
              onItemClick={onCrossAisleOptionClick}
              onClose={onCrossAisleCommit}
              displayValue={crossAislesDisplayValue}
              disabled={mode !== WorkspaceMode.EDITABLE}
            />
          </PropertiesLabel>
        </Stack>
      </Accordion>
    </Stack>
  );
}

export const HikTemplateParameters = memo(HikTemplateParametersComponent);
